import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  archiveRemoteEvent,
  remoteEventsPageQuery,
  remoteEventsTableQuery,
  type RemoteEventRow,
  type RemoteEventsView,
} from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { newAlert } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** odUtcWhen, fara moment.js - aceeasi functie ca pe Agent Log. */
export function utcWhen(raw: string): { text: string; title: string } {
  if (!raw) return { text: '', title: '' }
  const utc = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z')
  if (Number.isNaN(utc.getTime())) return { text: raw, title: '' }
  const pad = (n: number) => String(n).padStart(2, '0')
  const title = `${utc.getUTCFullYear()}-${pad(utc.getUTCMonth() + 1)}-${pad(utc.getUTCDate())} ${pad(utc.getUTCHours())}:${pad(utc.getUTCMinutes())}:${pad(utc.getUTCSeconds())} UTC`
  const text = `${pad(utc.getDate())} ${MONTHS[utc.getMonth()]} ${utc.getFullYear()} ${pad(utc.getHours())}:${pad(utc.getMinutes())}:${pad(utc.getSeconds())}`
  return { text, title }
}

/**
 * Fila Remote Event Viewer - opteemum/devices/details/remote_event_viewer.php
 * plus coloanele RemoteEventsModel.
 *
 * Coloana Archive e cea din `odArchiveEventColumn` (od_ux.js): pe lista vie
 * pune grupul deoparte, pe cea arhivata il aduce inapoi; posteaza la
 * `/devices/{id}/remote-events/archive` (Opteemum::archiveRemoteEvent) cu
 * mesajul EXACT cum e - din el se face amprenta pe server - si redeseneaza
 * lista si cifra de pe butonul Archived. AI ramane in portal.
 */

const TABLE = 'deviceRemoteEvents'
const EV_CSS = `
.od-ev-views {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin: 0 0 10px;
}
.od-ev-views .js-events-view.is-on {
    background: var(--od-ink, #1f2933);
    border-color: var(--od-ink, #1f2933);
    color: #fff;
}
.od-ev-count {
    display: inline-block;
    min-width: 16px;
    margin-left: 4px;
    padding: 0 5px;
    border-radius: 8px;
    background: rgba(0, 0, 0, .12);
    font-size: 10px;
    font-weight: 700;
    line-height: 15px;
}
.od-ev-views .js-events-view.is-on .od-ev-count {
    background: rgba(255, 255, 255, .25);
}
.od-ev-hint {
    flex: 1 1 260px;
    min-width: 0;
    font-size: 11px;
    line-height: 1.4;
    color: var(--od-ink-faint, #6b7480);
}
`

export function lastSeenHtml(row: Pick<RemoteEventRow, 'time' | 'first_time' | 'occurrences'>): string {
  const last = utcWhen(row.time)
  const n = row.occurrences || 1
  if (n <= 1 || !row.first_time) {
    return last.text
  }
  const first = utcWhen(row.first_time)
  return `${last.text}<div class="color-light-grey font-10">first ${first.text}</div>`
}

export function DeviceRemoteEventViewer({ id, open }: ScreenProps) {
  const page = useQuery(remoteEventsPageQuery(id))
  const [view, setView] = useState<RemoteEventsView>('live')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 4, dir: 'desc' })
  const [openRow, setOpenRow] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const queryClient = useQueryClient()

  const table = useQuery({
    ...remoteEventsTableQuery(id, {
      view,
      start,
      length,
      orderColumn: order.column,
      orderDir: order.dir,
      search,
    }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
  })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const switchView = (next: RemoteEventsView) => {
    setView(next)
    setStart(0)
    setOpenRow(null)
  }

  const archive = async (row: RemoteEventRow) => {
    if (busy) return
    setBusy(true)
    const undo = view === 'archived'
    try {
      const res = await archiveRemoteEvent(id, { category: row.category, log_id: row.log_id, title: row.title, message: row.message }, undo)
      if (res.status !== 'OK') {
        newAlert(res.message || 'It could not be archived.', 'danger', 6000)
        return
      }
      newAlert(undo ? 'Back in the live list.' : 'Put aside.', 'success', 4000)
      await queryClient.invalidateQueries({ queryKey: ['device', id, 'remote-events'] })
    } catch {
      newAlert('It could not be archived.', 'danger', 6000)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="row">
      <style>{EV_CSS}</style>
      <div className="col-md-12">
        <p className="text-muted" style={{ margin: '0 0 12px', fontSize: 12 }}>
          Errors from the machine itself: Event Viewer on Windows, the system journal on Linux.
          Identical lines are grouped; Count is how many times that error repeated.
          Failures of the OptimumDesk agent are{' '}
          <a
            href="javascript:void(0)"
            className="js-open-agent-log"
            onClick={(e) => {
              e.preventDefault()
              open('overview/agentSettings/')
            }}
          >
            Agent Log
          </a>
          .
        </p>
        <div className="od-ev-views">
          <button
            type="button"
            className={`btn btn-default btn-xs js-events-view${view === 'live' ? ' is-on' : ''}`}
            data-view="live"
            onClick={() => switchView('live')}
          >
            Live
          </button>
          <button
            type="button"
            className={`btn btn-default btn-xs js-events-view${view === 'archived' ? ' is-on' : ''}`}
            data-view="archived"
            onClick={() => switchView('archived')}
          >
            Archived <span className="od-ev-count">{page.data.archived_count}</span>
          </button>
          <span className="od-ev-hint">
            Archiving puts an error aside without deleting anything &mdash;
            and it comes back on its own if the error happens again.
          </span>
        </div>
        <EventsTable
          view={view}
          onArchive={(row) => void archive(row)}
          rows={table.data?.data ?? []}
          total={table.data?.recordsFiltered ?? 0}
          empty={page.data.empty}
          fetching={table.isFetching}
          error={table.isError ? (table.error as Error).message : null}
          start={start}
          length={length}
          search={search}
          order={order}
          openRow={openRow}
          onStart={setStart}
          onLength={(n) => {
            setLength(n)
            setStart(0)
          }}
          onSearch={(q) => {
            setSearch(q)
            setStart(0)
          }}
          onOrder={(next) => {
            setOrder(next)
            setStart(0)
          }}
          onToggle={(rowId) => setOpenRow((cur) => (cur === rowId ? null : rowId))}
        />
      </div>
    </div>
  )
}

const COLUMNS: { key: string; title: string; orderable: boolean; width?: number; cls?: string }[] = [
  { key: 'source', title: 'Source', orderable: true },
  { key: 'category', title: 'Category', orderable: true, width: 110 },
  { key: 'log_id', title: 'Event ID', orderable: true, width: 90 },
  { key: 'count', title: 'Count', orderable: true, width: 70, cls: 'td-small a-center' },
  { key: 'last_seen', title: 'Last seen', orderable: true, width: 180 },
  { key: 'archive', title: 'Archive', orderable: false, width: 70, cls: 'td-small a-center' },
]

function cell(row: RemoteEventRow, key: string, open: boolean): string {
  if (key === 'last_seen') return lastSeenHtml(row)
  if (key === 'source' && open) return row.source.replace('fa-chevron-down', 'fa-chevron-up')
  return String(row[key as keyof RemoteEventRow] ?? '')
}

function EventsTable({
  view,
  onArchive,
  rows,
  total,
  empty,
  fetching,
  error,
  start,
  length,
  search,
  order,
  openRow,
  onStart,
  onLength,
  onSearch,
  onOrder,
  onToggle,
}: {
  view: RemoteEventsView
  onArchive(row: RemoteEventRow): void
  rows: RemoteEventRow[]
  total: number
  empty: string
  fetching: boolean
  error: string | null
  start: number
  length: number
  search: string
  order: { column: number; dir: 'asc' | 'desc' }
  openRow: string | null
  onStart(n: number): void
  onLength(n: number): void
  onSearch(q: string): void
  onOrder(next: { column: number; dir: 'asc' | 'desc' }): void
  onToggle(rowId: string): void
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <div className="row">
        <div className="col-sm-6 dataTableF">
          <div className="dataTables_filter" id={`${TABLE}_filter`}>
            <label>
              Search:{' '}
              <input
                type="search"
                className="form-control input-sm"
                aria-controls={TABLE}
                value={search}
                onChange={(e) => onSearch(e.target.value)}
              />
            </label>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table className="table table-striped table-hover dataTable remote-events-table no-footer" width="100%" id={TABLE} role="grid">
            <thead>
              <tr role="row">
                {COLUMNS.map((col, i) => {
                  const sorted = order.column === i
                  const cls = [
                    col.cls ?? 'td-small a-left',
                    col.orderable ? (sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting') : 'sorting_disabled',
                  ].join(' ')
                  return (
                    <th
                      key={col.key}
                      className={cls}
                      style={col.width ? { width: col.width } : undefined}
                      aria-label={`${col.title}: activate to sort column ${sorted && order.dir === 'asc' ? 'descending' : 'ascending'}`}
                      aria-sort={sorted ? (order.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      onClick={() => {
                        if (!col.orderable) return
                        onOrder({ column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' })
                      }}
                    >
                      {col.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={6} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={6} className="dataTables_empty">
                    {empty}
                  </td>
                </tr>
              ) : (
                rows.flatMap((row, i) => {
                  const shown = openRow === row.DT_RowId
                  const main = (
                    <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                      {COLUMNS.map((col) =>
                        col.key === 'archive' ? (
                          <td key={col.key} className={col.cls}>
                            <a
                              href="javascript:void(0)"
                              className="js-archive-event"
                              data-undo={view === 'archived' ? '1' : '0'}
                              title={view === 'archived' ? 'Put it back in the live list' : 'Put it aside. It comes back on its own if the error happens again.'}
                              onClick={(e) => {
                                e.preventDefault()
                                onArchive(row)
                              }}
                            >
                              <i className={`fas ${view === 'archived' ? 'fa-undo' : 'fa-archive'}`}></i>
                            </a>
                          </td>
                        ) : (
                        <td
                          key={col.key}
                          className={col.cls ?? 'td-small a-left'}
                          dangerouslySetInnerHTML={{ __html: cell(row, col.key, shown) }}
                          onClick={
                            col.key === 'source'
                              ? (e) => {
                                  const a = (e.target as HTMLElement).closest('a.js-expand-event')
                                  if (!a) return
                                  e.preventDefault()
                                  onToggle(row.DT_RowId)
                                }
                              : undefined
                          }
                        />
                        ),
                      )}
                    </tr>
                  )
                  if (!shown) return [main]
                  return [
                    main,
                    <tr key={`${row.DT_RowId}-child`} className="child">
                      <td colSpan={6}>
                        <div style={{ padding: '8px 12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {row.message}
                        </div>
                      </td>
                    </tr>,
                  ]
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select
                name={`${TABLE}_length`}
                aria-controls={TABLE}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
